import { useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { AppContext } from 'contexts/AppProvider'
import { buffetSelector } from 'store/slices/buffetSlice'
import {
  useBuffetFpCheckoutQuery,
  useBuffetHhCheckoutQuery,
  useInitiateCheckoutMutation,
  BuffetItemTypeEnum,
  PaymentTypeEnum,
  OrderStatusEnum,
  type BuffetFpCheckoutQuery,
  type BuffetHhCheckoutQuery,
  useUpdateOrderPaymentMethodMutation,
} from 'graphQL/dinein/generate/operations'
import { CustomerPaymentTypeEnum } from 'graphQL/hatostore/generate/operations'
import useValidateCurrentOrder, { OrderInvalidEnum as BuffetInvalidEnum } from 'hooks/buffet/useValidateCurrentOrder'
import { dineInClient } from 'variables/services'

export type CurrentOrder =
  | BuffetFpCheckoutQuery['orderAndFPMemberCoupons']['order']
  | BuffetHhCheckoutQuery['orderAndHHMemberCoupons']['order']

interface BuffetItem {
  buffetItemType: BuffetItemTypeEnum
  totalSatangs: number
}

export interface Vendor {
  id: number
  nameEn: string
  nameTh: string
  logoImage: { resizableURL: string; originalURL: string }
}
type UseBuffetCheckoutProps = {}

const useBuffetCheckout = (props: UseBuffetCheckoutProps) => {
  const { brand, currentUser, isFoodPassion } = useContext(AppContext)
  const buffet = useSelector(buffetSelector)
  const [currentOrder, setCurrentOrder] = useState<CurrentOrder>()
  const [vendor, setVendor] = useState<Vendor>()
  const [buffetItems, setBuffetItems] = useState<BuffetItem[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentTypeEnum[]>([])

  const { handleCheckoutValidation } = useValidateCurrentOrder()

  const [initiateCheckout] = useInitiateCheckoutMutation({
    client: dineInClient,
  })

  const [updateOrderPaymentMethod] = useUpdateOrderPaymentMethodMutation({
    client: dineInClient,
    onCompleted: ({ updateOrderPaymentMethod: { order } }) => {
      if (currentOrder) {
        const { omiseCardID, paymentPhone, paymentType } = order
        setCurrentOrder({ ...currentOrder, omiseCardID, paymentPhone, paymentType })
      }
    },
  })

  const { refetch: refetcBuffetFpCheckout } = useBuffetFpCheckoutQuery({
    client: dineInClient,
    fetchPolicy: 'no-cache',
    skip: !isFoodPassion,
    variables: {
      vendorID: Number(brand?.id),
      orderID: buffet.table.orderID,
      tableID: buffet.table.tableID,
    },
    onCompleted: (result) => handleBuffetCheckoutQueryResult(result, result.orderAndFPMemberCoupons?.order),
  })

  const { refetch: refetchBuffetHhCheckout } = useBuffetHhCheckoutQuery({
    client: dineInClient,
    fetchPolicy: 'no-cache',
    skip: isFoodPassion,
    variables: {
      vendorID: Number(brand?.id),
      orderID: buffet.table.orderID,
      tableID: buffet.table.tableID,
    },
    onCompleted: (result) => handleBuffetCheckoutQueryResult(result, result.orderAndHHMemberCoupons?.order),
  })

  const refreshBuffetOrder = isFoodPassion ? refetcBuffetFpCheckout : refetchBuffetHhCheckout

  const handleBuffetCheckoutQueryResult = async (
    result: BuffetFpCheckoutQuery | BuffetHhCheckoutQuery,
    orderData: CurrentOrder
  ) => {
    const { vendor, vendorSetting } = result
    if (orderData) {
      setCurrentOrder({ ...orderData })
    }
    setBuffetItems(orderData.buffetItems)
    setVendor(vendor)
    setPaymentMethods(vendorSetting.paymentMethods)
    const validateResult = handleCheckoutValidation(buffet.currentOrder)
    const autoInitiateCheckout = validateResult.invalid === BuffetInvalidEnum.ReadyToCheckout && orderData.userID === 0
    if (autoInitiateCheckout) {
      await initiateCheckout({
        variables: {
          orderID: buffet.table.orderID,
          override: true,
        },
      })
      await refreshBuffetOrder()
    } else if (
      !orderData.paymentType &&
      orderData.userID === currentUser.id &&
      currentUser.defaultPaymentMethod &&
      ![PaymentTypeEnum.None, CustomerPaymentTypeEnum.CashOnDelivery].includes(currentUser.defaultPaymentMethod)
    ) {
      if (
        ((currentUser.defaultPaymentMethod === PaymentTypeEnum.OmiseCreditCard && currentUser?.omiseCardID) ||
          currentUser.defaultPaymentMethod !== PaymentTypeEnum.OmiseCreditCard) &&
        orderData.status !== OrderStatusEnum.StaffCheckout
      ) {
        await updateOrderPaymentMethod({
          variables: {
            orderID: orderData.id,
            cardID: currentUser?.omiseCardID,
            type: currentUser.defaultuserProfilePaymentMethod ?? currentUser.defaultPaymentMethod,
            phoneNumber: currentUser?.defaultPaymentPhone,
          },
        })
        await refreshBuffetOrder()
      }
    }
  }

  return {
    currentOrder,
    setCurrentOrder,
    vendor,
    buffetItems,
    paymentMethods,
    refreshBuffetOrder,
    handleCheckoutValidation,
    refetcBuffetFpCheckout,
    refetchBuffetHhCheckout,
  }
}

export default useBuffetCheckout
